import {
  NETWORK_PALLET_TYPES,
  PalletDimensions,
  findPalletType,
  isKnownPalletType,
  isStandardPalletType,
  lockFootprint,
} from './types'

interface Props {
  dimensions: PalletDimensions
  palletType: string | undefined
  onDimensionsChange: (dimensions: PalletDimensions) => void
  onPalletTypeChange: (palletType: string) => void
}

const LIMITS = {
  length: { min: 0.4, max: 2.4, step: 0.1, default: 1.2 },
  width: { min: 0.4, max: 2.4, step: 0.1, default: 1.0 },
  height: { min: 0.3, max: 2.4, step: 0.05, default: 1.0 },
  weight: { min: 50, max: 2000, step: 10, default: 500 },
}

const cmToM = (cm: number | null | undefined): number | null => (cm == null ? null : cm / 100)
const mToCm = (m: number | null): number | null => (m == null ? null : Math.round(m * 100))

const SCALE = 50
const ANGLE_RAD = Math.PI / 4
const DEPTH_SCALE = 0.5
const DECK_HEIGHT_M = 0.145
const DECK_HEIGHT_PX = DECK_HEIGHT_M * SCALE
const SVG_PADDING = 12
const CAP_LABEL_WIDTH = 72

const formatMetres = (metres: number) => `${metres.toFixed(Math.round(metres * 100) % 10 === 0 ? 1 : 2)}m`

const describePalletType = (value: string): string => {
  const type = findPalletType(value)
  if (!type?.footprint || !type.defaultCaps) return value
  return `${value} · ${type.footprint.lengthCm / 100} × ${type.footprint.widthCm / 100}m · ≤ ${type.defaultCaps.heightCm / 100}m · ≤ ${type.defaultCaps.weightKg}kg`
}

export function PalletVisualizer({ dimensions, palletType, onDimensionsChange, onPalletTypeChange }: Props) {
  const isStandard = isStandardPalletType(palletType)
  const isLegacyType = !!palletType && !isKnownPalletType(palletType)

  const lengthM = cmToM(dimensions.depth) ?? LIMITS.length.default
  const widthM = cmToM(dimensions.width) ?? LIMITS.width.default
  const heightM = cmToM(dimensions.height) ?? LIMITS.height.default
  const weightKg = dimensions.weight ?? LIMITS.weight.default

  const updateLength = (field: 'depth' | 'width' | 'height', metres: number) =>
    onDimensionsChange(lockFootprint(palletType, { ...dimensions, [field]: mToCm(metres) }))
  const updateWeight = (kg: number) => onDimensionsChange({ ...dimensions, weight: kg })

  const frontM = Math.max(lengthM, widthM)
  const sideM = Math.min(lengthM, widthM)

  const maxFrontPx = LIMITS.length.max * SCALE
  const maxDepthX = maxFrontPx * DEPTH_SCALE * Math.cos(ANGLE_RAD)
  const maxDepthY = maxFrontPx * DEPTH_SCALE * Math.sin(ANGLE_RAD)
  const svgW = Math.ceil(maxFrontPx + maxDepthX + SVG_PADDING * 2 + CAP_LABEL_WIDTH)
  const svgH = Math.ceil(LIMITS.height.max * SCALE + maxDepthY + DECK_HEIGHT_PX + SVG_PADDING * 2)

  const pxFront = frontM * SCALE
  const pxSide = sideM * SCALE
  const pxHeight = heightM * SCALE
  const depthX = pxSide * DEPTH_SCALE * Math.cos(ANGLE_RAD)
  const depthY = pxSide * DEPTH_SCALE * Math.sin(ANGLE_RAD)

  const offsetX = SVG_PADDING + (maxFrontPx + maxDepthX - (pxFront + depthX)) / 2
  const deckBottomY = svgH - SVG_PADDING
  const deckTopY = deckBottomY - DECK_HEIGHT_PX

  const frontBottomLeft: [number, number] = [offsetX, deckTopY]
  const frontBottomRight: [number, number] = [offsetX + pxFront, deckTopY]
  const frontTopRight: [number, number] = [offsetX + pxFront, deckTopY - pxHeight]
  const frontTopLeft: [number, number] = [offsetX, deckTopY - pxHeight]
  const backBottomRight: [number, number] = [offsetX + pxFront + depthX, deckTopY - depthY]
  const backTopRight: [number, number] = [offsetX + pxFront + depthX, deckTopY - pxHeight - depthY]
  const backTopLeft: [number, number] = [offsetX + depthX, deckTopY - pxHeight - depthY]

  const deckFrontTopLeft: [number, number] = [offsetX, deckTopY]
  const deckFrontTopRight: [number, number] = [offsetX + pxFront, deckTopY]
  const deckFrontBottomLeft: [number, number] = [offsetX, deckBottomY]
  const deckFrontBottomRight: [number, number] = [offsetX + pxFront, deckBottomY]
  const deckBackTopRight: [number, number] = [offsetX + pxFront + depthX, deckTopY - depthY]
  const deckBackBottomRight: [number, number] = [offsetX + pxFront + depthX, deckBottomY - depthY]

  const points = (vertices: [number, number][]) => vertices.map((p) => p.join(',')).join(' ')

  const scoringLines: number[] = []
  for (let metres = 0.5; metres < heightM - 0.01; metres += 0.5) scoringLines.push(metres)

  const capLabelX = backTopRight[0] + 8
  const capLabelY = backTopRight[1] + 4

  const inputClass =
    'w-16 px-1.5 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 text-right'

  const selectClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div>
      <label className='block text-xs font-medium text-gray-500 mb-2'>Pallet type</label>

      <select
        value={palletType ?? ''}
        onChange={(e) => onPalletTypeChange(e.target.value)}
        className={selectClass}
      >
        {!palletType && <option value=''>Pick a pallet type</option>}
        {isLegacyType && (
          <option value={palletType} disabled>
            {palletType} (not recognised — pick a type)
          </option>
        )}
        {NETWORK_PALLET_TYPES.map((type) => (
          <option key={type.value} value={type.value}>
            {describePalletType(type.value)}
          </option>
        ))}
      </select>

      <div className='flex justify-center my-3'>
        <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
          <defs>
            <linearGradient id='palletBoxTop' x1='0%' y1='0%' x2='0%' y2='100%'>
              <stop offset='0%' stopColor='#fef9c3' />
              <stop offset='100%' stopColor='#fde68a' />
            </linearGradient>
            <linearGradient id='palletBoxFront' x1='0%' y1='0%' x2='0%' y2='100%'>
              <stop offset='0%' stopColor='#fcd34d' />
              <stop offset='100%' stopColor='#f59e0b' />
            </linearGradient>
            <linearGradient id='palletBoxSide' x1='0%' y1='0%' x2='100%' y2='0%'>
              <stop offset='0%' stopColor='#d97706' />
              <stop offset='100%' stopColor='#b45309' />
            </linearGradient>
            <linearGradient id='palletWoodFront' x1='0%' y1='0%' x2='0%' y2='100%'>
              <stop offset='0%' stopColor='#a16207' />
              <stop offset='100%' stopColor='#713f12' />
            </linearGradient>
            <linearGradient id='palletWoodSide' x1='0%' y1='0%' x2='100%' y2='0%'>
              <stop offset='0%' stopColor='#713f12' />
              <stop offset='100%' stopColor='#451a03' />
            </linearGradient>
          </defs>

          <polygon
            points={points([deckFrontTopRight, deckBackTopRight, deckBackBottomRight, deckFrontBottomRight])}
            fill='url(#palletWoodSide)'
            stroke='#451a03'
            strokeWidth={0.75}
            strokeLinejoin='round'
          />
          <polygon
            points={points([deckFrontTopLeft, deckFrontTopRight, deckFrontBottomRight, deckFrontBottomLeft])}
            fill='url(#palletWoodFront)'
            stroke='#451a03'
            strokeWidth={0.75}
            strokeLinejoin='round'
          />
          <line
            x1={deckFrontBottomLeft[0]}
            y1={deckFrontBottomLeft[1] - DECK_HEIGHT_PX * 0.45}
            x2={deckFrontBottomRight[0]}
            y2={deckFrontBottomRight[1] - DECK_HEIGHT_PX * 0.45}
            stroke='#451a03'
            strokeWidth={0.6}
            opacity={0.45}
          />

          <polygon
            points={points([frontBottomRight, backBottomRight, backTopRight, frontTopRight])}
            fill='url(#palletBoxSide)'
            stroke='#92400e'
            strokeWidth={0.9}
            strokeLinejoin='round'
          />
          <polygon
            points={points([frontTopLeft, frontTopRight, backTopRight, backTopLeft])}
            fill='url(#palletBoxTop)'
            stroke='#92400e'
            strokeWidth={0.9}
            strokeLinejoin='round'
          />
          <polygon
            points={points([frontBottomLeft, frontBottomRight, frontTopRight, frontTopLeft])}
            fill='url(#palletBoxFront)'
            stroke='#92400e'
            strokeWidth={0.9}
            strokeLinejoin='round'
          />

          {scoringLines.map((metres) => (
            <line
              key={metres}
              x1={frontBottomLeft[0]}
              y1={deckTopY - metres * SCALE}
              x2={frontBottomRight[0]}
              y2={deckTopY - metres * SCALE}
              stroke='#92400e'
              strokeWidth={0.5}
              opacity={0.3}
            />
          ))}

          <text x={capLabelX} y={capLabelY} fill='#b45309' fontSize={12} fontWeight={600}>
            ≤ {formatMetres(heightM)}
          </text>
          <text x={capLabelX} y={capLabelY + 15} fill='#6b7280' fontSize={11}>
            ≤ {Math.round(weightKg)}kg
          </text>
        </svg>
      </div>

      <div className='text-center text-xs text-gray-400 mb-3'>
        {formatMetres(lengthM)} &times; {formatMetres(widthM)} &times; {formatMetres(heightM)} (L &times; W &times; H) &middot; &le; {Math.round(weightKg)}kg
      </div>

      <div className='space-y-3'>
        {isStandard ? (
          <div className='space-y-2'>
            <ReadOnlyRow label='Length' value={formatMetres(lengthM)} />
            <ReadOnlyRow label='Width' value={formatMetres(widthM)} />
          </div>
        ) : (
          <>
            <DimensionRow
              label='Length'
              unit='m'
              value={lengthM}
              limits={LIMITS.length}
              inputClass={inputClass}
              onChange={(v) => updateLength('depth', v)}
            />
            <DimensionRow
              label='Width'
              unit='m'
              value={widthM}
              limits={LIMITS.width}
              inputClass={inputClass}
              onChange={(v) => updateLength('width', v)}
            />
          </>
        )}
        <DimensionRow
          label='Max height'
          unit='m'
          value={heightM}
          limits={LIMITS.height}
          inputClass={inputClass}
          onChange={(v) => updateLength('height', v)}
        />
        <DimensionRow
          label='Max weight'
          unit='kg'
          value={weightKg}
          limits={LIMITS.weight}
          inputClass={inputClass}
          onChange={updateWeight}
        />
      </div>
    </div>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between'>
      <span className='text-xs text-gray-400'>{label}</span>
      <span className='text-xs font-medium text-gray-600'>{value}</span>
    </div>
  )
}

function DimensionRow({
  label,
  unit,
  value,
  limits,
  inputClass,
  onChange,
}: {
  label: string
  unit: string
  value: number
  limits: { min: number; max: number; step: number }
  inputClass: string
  onChange: (value: number) => void
}) {
  const clamp = (v: number) => Math.min(limits.max, Math.max(limits.min, v))
  return (
    <div>
      <div className='flex items-center justify-between mb-1'>
        <label className='text-xs text-gray-400'>{label}</label>
        <div className='flex items-center gap-1'>
          <input
            type='number'
            value={value}
            onChange={(e) => {
              const v = parseFloat(e.target.value)
              if (!isNaN(v)) onChange(clamp(v))
            }}
            className={inputClass}
            step={limits.step}
            min={limits.min}
            max={limits.max}
          />
          <span className='text-xs text-gray-400 w-5'>{unit}</span>
        </div>
      </div>
      <input
        type='range'
        min={limits.min}
        max={limits.max}
        step={limits.step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className='w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-600'
      />
    </div>
  )
}
